import React, { useState, useEffect, useCallback } from 'react';
import usePageTranslation from '../../../shared/hooks/usePageTranslation';
import { Batiment, Personnel, Vehicule } from '../../../shared/types/game';
import BuildingInfoTab from './BuildingDetailModal/BuildingInfoTab';
import BuildingPersonnelTab from './BuildingDetailModal/BuildingPersonnelTab';
import BuildingVehiculesTab from './BuildingDetailModal/BuildingVehiculesTab';
import BuildingDetailsTab from './BuildingDetailModal/BuildingDetailsTab';
import BuildingDetailsCTATab from './BuildingDetailModal/BuildingDetailsCTATab';
import BuildingCellulesTab from './BuildingDetailModal/BuildingCellulesTab';
import BuildingLitsTab from './BuildingDetailModal/BuildingLitsTab';
import BuildingCTATab from './BuildingDetailModal/BuildingCTATab';
import { useBuildingCapacity } from './BuildingDetailModal/useBuildingCapacity';
import {
  getBuildingIconPath,
  getBuildingIconConfig,
  fetchBuildingPersonnel,
  fetchBuildingVehicules,
} from './BuildingDetailModal/buildingDetailHelpers';

export interface BuildingDetailModalProps {
  building: Batiment;
  address?: string;
  onClose: () => void;
  onBuildingUpdate?: (updatedBuilding: Batiment) => void;
}

const BuildingDetailModal: React.FC<BuildingDetailModalProps> = ({ building, address, onClose, onBuildingUpdate }) => {
  const { t } = usePageTranslation('game', 'Map');
  const [activeTab, setActiveTab] = useState<'info' | 'personnel' | 'vehicules' | 'cellules' | 'lits' | 'details' | 'cta'>('info');
  const [personnel, setPersonnel] = useState<Personnel[]>([]);
  const [vehicules, setVehicules] = useState<Vehicule[]>([]);
  const [loadingPersonnel, setLoadingPersonnel] = useState(false);
  const [loadingVehicules, setLoadingVehicules] = useState(false);
  const [buildingData, setBuildingData] = useState<Batiment>(building);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(building.nom);

  const handleBuildingUpdate = useCallback(
    (updated: Batiment) => {
      setBuildingData(updated);
      onBuildingUpdate?.(updated);
    },
    [onBuildingUpdate]
  );

  const {
    calculatedLevel,
    totalCapacity,
    totalVehiclesCapacity,
    timeRemainingPersonnel,
    timeRemainingVehicules,
    handleStartAugmentation
  } = useBuildingCapacity(buildingData, setBuildingData, handleBuildingUpdate);

  // Fonction pour sauvegarder les modifications du nom
  const handleSaveName = () => {
    const updated = {
      ...buildingData,
      nom: editedName,
      updated_at: new Date().toISOString()
    };
    handleBuildingUpdate(updated);
    setIsEditingName(false);
  };

  // Obtenir le chemin de l'icône pour le bâtiment
  const serviceIconPath = getBuildingIconPath(buildingData.type);

  // Charger le personnel et les véhicules associés au bâtiment
  useEffect(() => {
    const loadPersonnel = async () => {
      setLoadingPersonnel(true);
      try {
        const list = await fetchBuildingPersonnel(buildingData.id);
        if (list) setPersonnel(list);
      } catch (error) {
        console.error('Erreur lors du chargement du personnel:', error);
      } finally {
        setLoadingPersonnel(false);
      }
    };

    const loadVehicules = async () => {
      setLoadingVehicules(true);
      try {
        const list = await fetchBuildingVehicules(buildingData.id);
        if (list) setVehicules(list);
      } catch (error) {
        console.error('Erreur lors du chargement des véhicules:', error);
      } finally {
        setLoadingVehicules(false);
      }
    };

    loadPersonnel();
    loadVehicules();
  }, [buildingData.id]);

  // Fermer avec Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-gray-900 rounded-lg shadow-2xl w-full max-w-5xl max-h-[85vh] overflow-hidden border border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700 bg-gray-800/50">
          <div className="flex items-center gap-4 flex-1">
            <div 
              className="rounded-full flex items-center justify-center border-2 border-white shadow-md flex-shrink-0"
              style={{ 
                backgroundColor: getBuildingIconConfig(buildingData.type).backgroundColor,
                width: '40px',
                height: '40px'
              }}
            >
              <img src={serviceIconPath} alt={buildingData.type} className="w-8 h-8" />
            </div>
            <div className="flex-1">
              {isEditingName ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    onBlur={handleSaveName}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSaveName();
                      } else if (e.key === 'Escape') {
                        setEditedName(buildingData.nom);
                        setIsEditingName(false);
                      }
                    }}
                    className="text-2xl font-bold bg-gray-700 text-white px-3 py-1 rounded border border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autoFocus
                  />
                </div>
              ) : (
                <h2 
                  className="text-2xl font-bold text-white cursor-pointer hover:text-blue-400 transition-colors"
                  onClick={() => setIsEditingName(true)}
                  title="Cliquez pour modifier le nom"
                >
                  {buildingData.nom}
                  <svg className="inline-block w-4 h-4 ml-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </h2>
              )}
              <p className="text-sm text-gray-400 mt-1">
                {t(`contextMenu.buildingTypes.${buildingData.type}`)}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg"
            aria-label="Fermer"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex min-h-0 flex-wrap border-b border-gray-700 bg-gray-800/30 sm:flex-nowrap">
          <button
            type="button"
            onClick={() => setActiveTab('info')}
            className={`flex-1 px-4 py-3 text-sm font-medium sm:px-6 ${
              activeTab === 'info'
                ? 'text-blue-400 border-b-2 border-blue-400 bg-gray-800/50'
                : 'text-gray-400 hover:text-white hover:bg-gray-800/30'
            }`}
          >
            Informations
          </button>
          {buildingData.type !== 'cta' && (
            <button
              type="button"
              onClick={() => setActiveTab('personnel')}
              className={`flex-1 px-4 py-3 text-sm font-medium sm:px-6 relative ${
                activeTab === 'personnel'
                  ? 'text-blue-400 border-b-2 border-blue-400 bg-gray-800/50'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800/30'
              }`}
            >
              Personnel
              {personnel.length > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs bg-blue-500/20 text-blue-400 rounded-full">
                  {personnel.length}
                </span>
              )}
            </button>
          )}
          {buildingData.type !== 'cta' && (
            <button
              type="button"
              onClick={() => setActiveTab('vehicules')}
              className={`flex-1 px-4 py-3 text-sm font-medium sm:px-6 relative ${
                activeTab === 'vehicules'
                  ? 'text-blue-400 border-b-2 border-blue-400 bg-gray-800/50'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800/30'
              }`}
            >
              Véhicules
              {vehicules.length > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs bg-blue-500/20 text-blue-400 rounded-full">
                  {vehicules.length}
                </span>
              )}
            </button>
          )}
          {(buildingData.type === 'prison' || buildingData.type === 'fdo') && (
            <button
              type="button"
              onClick={() => setActiveTab('cellules')}
              className={`flex-1 px-4 py-3 text-sm font-medium sm:px-6 relative ${
                activeTab === 'cellules'
                  ? 'text-blue-400 border-b-2 border-blue-400 bg-gray-800/50'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800/30'
              }`}
            >
              Cellules
              {(buildingData.capacite || 0) > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs bg-blue-500/20 text-blue-400 rounded-full">
                  {buildingData.capacite || 0}
                </span>
              )}
            </button>
          )}
          {buildingData.type === 'hopital' && (
            <button
              type="button"
              onClick={() => setActiveTab('lits')}
              className={`flex-1 px-4 py-3 text-sm font-medium sm:px-6 relative ${
                activeTab === 'lits'
                  ? 'text-blue-400 border-b-2 border-blue-400 bg-gray-800/50'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800/30'
              }`}
            >
              Chambres
              {(buildingData.capacite || 0) > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs bg-blue-500/20 text-blue-400 rounded-full">
                  {buildingData.capacite || 0}
                </span>
              )}
            </button>
          )}
          {buildingData.type === 'cta' && (
            <button
              type="button"
              onClick={() => setActiveTab('cta')}
              className={`flex-1 px-4 py-3 text-sm font-medium sm:px-6 relative ${
                activeTab === 'cta'
                  ? 'text-blue-400 border-b-2 border-blue-400 bg-gray-800/50'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800/30'
              }`}
            >
              Bâtiments liés
            </button>
          )}
          <button
            type="button"
            onClick={() => setActiveTab('details')}
            className={`flex-1 px-4 py-3 text-sm font-medium sm:px-6 ${
              activeTab === 'details'
                ? 'text-blue-400 border-b-2 border-blue-400 bg-gray-800/50'
                : 'text-gray-400 hover:text-white hover:bg-gray-800/30'
            }`}
          >
            Détails
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(85vh-160px)]">
          {activeTab === 'info' && (
            <BuildingInfoTab
              building={buildingData}
              address={address}
              calculatedLevel={calculatedLevel}
              totalCapacity={totalCapacity}
              totalVehiclesCapacity={totalVehiclesCapacity}
              timeRemainingPersonnel={timeRemainingPersonnel}
              timeRemainingVehicules={timeRemainingVehicules}
              handleStartAugmentation={handleStartAugmentation}
              onBuildingUpdate={handleBuildingUpdate}
            />
          )}

          {activeTab === 'personnel' && buildingData.type !== 'cta' && (
            <BuildingPersonnelTab
              personnel={personnel}
              loadingPersonnel={loadingPersonnel}
              buildingType={buildingData.type}
              buildingId={buildingData.id}
              buildingName={buildingData.nom}
              userId={buildingData.user_id}
              onPersonnelAdded={() => {
                (async () => {
                  setLoadingPersonnel(true);
                  try {
                    const list = await fetchBuildingPersonnel(buildingData.id);
                    if (list) setPersonnel(list);
                  } catch (error) {
                    console.error('Erreur lors du rechargement du personnel:', error);
                  } finally {
                    setLoadingPersonnel(false);
                  }
                })();
              }}
            />
          )}

          {activeTab === 'vehicules' && buildingData.type !== 'cta' && (
            <BuildingVehiculesTab
              vehicules={vehicules}
              loadingVehicules={loadingVehicules}
              buildingType={buildingData.type}
              buildingId={buildingData.id}
              userId={buildingData.user_id}
              onVehiculeAdded={() => {
                (async () => {
                  setLoadingVehicules(true);
                  try {
                    const list = await fetchBuildingVehicules(buildingData.id);
                    if (list) setVehicules(list);
                  } catch (error) {
                    console.error('Erreur lors du rechargement des véhicules:', error);
                  } finally {
                    setLoadingVehicules(false);
                  }
                })();
              }}
            />
          )}

          {activeTab === 'cellules' && (
            <BuildingCellulesTab
              building={buildingData}
              onIncreaseCapacity={buildingData.type === 'prison' ? () => handleStartAugmentation('personnel', 1) : undefined}
            />
          )}

          {activeTab === 'lits' && (
            <BuildingLitsTab
              building={buildingData}
            />
          )}

          {activeTab === 'cta' && buildingData.type === 'cta' && (
            <BuildingCTATab
              ctaBuilding={buildingData}
              onBuildingUpdate={handleBuildingUpdate}
            />
          )}

          {activeTab === 'details' && buildingData.type === 'cta' && (
            <BuildingDetailsCTATab ctaBuilding={buildingData} />
          )}
          {activeTab === 'details' && buildingData.type !== 'cta' && (
            <BuildingDetailsTab
              building={buildingData}
              personnel={personnel}
              vehicules={vehicules}
              calculatedLevel={calculatedLevel}
              totalCapacity={totalCapacity}
              totalVehiclesCapacity={totalVehiclesCapacity}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default BuildingDetailModal;

